import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import {
  useToast,
  Box,
  Select,
  Input,
  Button,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Heading,
  useColorModeValue,
  SimpleGrid,
  Spacer,
} from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import { IFieldId } from "../types/fieldInterface";
import { IDevice } from "../types/deviceInterface";

const fullDateTimeFormat: Intl.DateTimeFormatOptions = {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
};

type SeriesPoint = {
  timestamp: string;
  temperature: number | null;
  humidity: number | null;
  precipitation: number | null;
  risk: number | null;
};
type EventPoint = {
  start: string;
  end: string;
  peak_risk: number;
  avg_temp: number;
  duration: number;
};

const models = [
  { key: "apple_scab", label: "Apple Scab" },
  { key: "olive_spot", label: "Olive Spot" },
  { key: "fire_blight", label: "Fire Blight" },
  { key: "downy_mildew", label: "Downy Mildew" },
  { key: "powdery_mildew", label: "Powdery Mildew" },
  { key: "black_rot", label: "Black Rot" },
  { key: "anthracnose", label: "Anthracnose" },
  { key: "phomopsis", label: "Phomopsis" },
  { key: "phytophthora_infestans", label: "Phytophthora Infestans" },
];

const DiseasePredictionView: React.FC = () => {
  const api = useAxios();
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.900");

  const [fields, setFields] = useState<IFieldId[]>([]);
  const [devices, setDevices] = useState<IDevice[]>([]);
  const [fieldId, setFieldId] = useState<number>();
  const [deviceId, setDeviceId] = useState<number>();
  const [modelKey, setModelKey] = useState<string>(models[0].key);
  // start date for prediction
  const [startDate, setStartDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const [series, setSeries] = useState<SeriesPoint[]>([]);
  const [events, setEvents] = useState<EventPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [predicting, setPredicting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [fRes, dRes] = await Promise.all([
          api.get("agtech/fields/"),
          api.get("frontend/user-devices/"),
        ]);
        setFields(fRes.data);
        setDevices(dRes.data);
        if (fRes.data[0]) setFieldId(fRes.data[0].id);
        if (dRes.data[0]) setDeviceId(dRes.data[0].id);
      } catch {
        toast({
          title: "Error loading options",
          status: "error",
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const run = async () => {
    if (!fieldId || !deviceId) return;
    setPredicting(true);
    try {
      const resp = await api.get("agtech/disease-prediction", {
        params: {
          disease: modelKey,
          user_device_id: deviceId,
          field_id: fieldId,
          start_date: startDate,
        },
      });
      setSeries(resp.data.series);
      setEvents(resp.data.events);
    } catch {
      toast({
        title: "Error running prediction",
        status: "error",
        isClosable: true,
      });
    } finally {
      setPredicting(false);
    }
  };

  const chartData = {
    labels: series.map((p) => new Date(p.timestamp).toLocaleString(undefined, fullDateTimeFormat)),
    datasets: [
      {
        label: "Temperature",
        data: series.map((p) => p.temperature),
        borderColor: "red",
        yAxisID: "y1",
        fill: false,
      },
      {
        label: "Humidity",
        data: series.map((p) => p.humidity),
        borderColor: "blue",
        yAxisID: "y1",
        fill: false,
      },
      {
        label: "Precipitation",
        data: series.map((p) => p.precipitation),
        borderColor: "green",
        yAxisID: "y1",
        fill: false,
      },
      {
        label: "Risk",
        data: series.map((p) => p.risk),
        borderColor: "purple",
        yAxisID: "y2",
        fill: false,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        {
          id: "y1",
          type: "linear",
          position: "left",
          ticks: { beginAtZero: true },
        },
        {
          id: "y2",
          type: "linear",
          position: "right",
          ticks: { min: 0, max: 1 },
          gridLines: { drawOnChartArea: false },
        },
      ],
      xAxes: [{}],
    },
  };

  return (
    <Navbar>
      <Box maxW="1200px" mx="auto" p={4} bg={bg} borderRadius="md">
        <Heading mb={4}>Disease Prediction</Heading>
        <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4} mb={4}>
          {loading ? (
            <Skeleton height="40px" />
          ) : (
            <Select value={fieldId} onChange={(e) => setFieldId(+e.target.value)}>
              {fields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.field_name}
                </option>
              ))}
            </Select>
          )}
          {loading ? (
            <Skeleton height="40px" />
          ) : (
            <Select value={deviceId} onChange={(e) => setDeviceId(+e.target.value)}>
              {devices.map((d) => (
                <option key={d.device_id} value={d.device_id}>
                  {d.device_name}
                </option>
              ))}
            </Select>
          )}
          <Select value={modelKey} onChange={(e) => setModelKey(e.target.value)}>
            {models.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </Select>
          {/* start date input */}
          {loading ? (
            <Skeleton height="40px" />
          ) : (
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          )}
          <Button onClick={run} colorScheme="teal" isLoading={predicting}>
            Run
          </Button>
        </SimpleGrid>
        <Box height={400} mb={4} overflow="hidden" position="relative">
          <Line data={chartData} options={chartOptions} />
        </Box>
        <Spacer />
        <Box>
          <Heading size="md" mb={2}>
            High Risk Events
          </Heading>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Start</Th>
                <Th>End</Th>
                <Th>Peak Risk</Th>
                <Th>Avg Temp</Th>
                <Th>Duration</Th>
              </Tr>
            </Thead>
            <Tbody>
              {events.length === 0 ? (
                <Tr>
                  <Td colSpan={5}>
                    <Text>No events</Text>
                  </Td>
                </Tr>
              ) : (
                events.map((e, i) => (
                  <Tr key={i}>
                    <Td>{new Date(e.start).toLocaleString()}</Td>
                    <Td>{new Date(e.end).toLocaleString()}</Td>
                    <Td>{e.peak_risk.toFixed(2)}</Td>
                    <Td>{e.avg_temp.toFixed(1)}</Td>
                    <Td>{e.duration}</Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Navbar>
  );
};

export default DiseasePredictionView;
